import {
  BrowserMessageForResponse,
  BrowserMessageResponse,
  exchangeElectronMessage as exchangeBrowserElectron,
  ExchangeOptions,
} from 'electron-communication-lib/BrowserForApp';
import {useEffect} from 'react';

/**
 * Used to exchange messages with the electron application. See {@link exchangeBrowserElectron} for more docs.
 */
export function useBrowserMessageExchange<T extends BrowserMessageForResponse>({
  message,
  responsePayloadHandler,
  dependencies = [], // for safety measure. if someone wants to run it every render cycle they can pass undefined here
  exchangeOptions,
}: {
  message: T;
  responsePayloadHandler: (response: BrowserMessageResponse[T['type']]) => void;
  dependencies?: unknown[];
  exchangeOptions?: ExchangeOptions;
}): void {
  useEffect(() => {
    const cancelExchange = exchangeBrowserElectron(
      message,
      responsePayloadHandler,
      exchangeOptions
    );

    return cancelExchange;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
}

/**
 * Used to specifically listen to a certain message type and relay the message to given function
 */
export const useMessageListener = <T extends BrowserMessageForResponse>(
  message: T,
  responsePayloadHandler: (response: BrowserMessageResponse[T['type']]) => void
): void => {
  useEffect(() => {
    const handleEvent = (event: MessageEvent) => {
      if (
        event.data?.type === 'renderer' &&
        event.data?.payload.type === message.type
      ) {
        responsePayloadHandler(event.data.payload);
      }
    };

    window.addEventListener('message', handleEvent);
    return () => window.removeEventListener('message', handleEvent);
  }, [message, responsePayloadHandler]);
};
